/*
 * Zeus Back in Stock
 * ------------------
 * Custom element <back-in-stock> that subscribes an email to Klaviyo's
 * client-side Back in Stock API for a given Shopify variant.
 *
 * All user-facing copy is passed in via data-* attributes (sourced from
 * locale files in the Liquid snippet) so messages stay translated.
 *
 * Klaviyo client BIS endpoint:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=XXXXXX
 */
(function () {
  "use strict";

  var KLAVIYO_ENDPOINT = "https://a.klaviyo.com/client/back-in-stock-subscriptions/";
  var KLAVIYO_REVISION = "2024-10-15";
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  class BackInStock extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("[data-bis-email]");
      this.button = this.querySelector("[data-bis-submit]");
      this.status = this.querySelector("[data-bis-status]");
      if (!this.input || !this.button) return;

      this._onSubmit = this.handleSubmit.bind(this);
      this._onKeydown = (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.handleSubmit();
        }
      };
      this.button.addEventListener("click", this._onSubmit);
      this.input.addEventListener("keydown", this._onKeydown);
    }

    disconnectedCallback() {
      if (this.button && this._onSubmit) {
        this.button.removeEventListener("click", this._onSubmit);
      }
      if (this.input && this._onKeydown) {
        this.input.removeEventListener("keydown", this._onKeydown);
      }
    }

    setStatus(message, type) {
      if (!this.status) return;
      this.status.textContent = message || "";
      this.status.classList.remove("bis-form__status--success", "bis-form__status--error");
      if (type) this.status.classList.add("bis-form__status--" + type);
    }

    handleSubmit() {
      if (this._busy) return;

      var email = (this.input.value || "").trim();
      if (!EMAIL_RE.test(email)) {
        this.input.setAttribute("aria-invalid", "true");
        this.input.focus();
        this.setStatus(this.dataset.bisInvalidEmail || this.dataset.bisError, "error");
        return;
      }

      this.input.setAttribute("aria-invalid", "false");

      var companyId = this.dataset.bisCompanyId;
      var variantId = this.dataset.bisVariantId;
      if (!companyId || !variantId) {
        // Setting not filled yet (Klaviyo company_id) or missing variant.
        console.warn("[zeus-bis] missing company_id or variant_id; subscription skipped");
        this.setStatus(this.dataset.bisError, "error");
        return;
      }

      this._busy = true;
      this.button.disabled = true;
      this.setStatus("", null);

      var payload = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            channels: ["EMAIL"],
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email,
                  locale: this.dataset.bisLocale || undefined,
                  properties: {
                    zeus_bis_language: this.dataset.bisLocale || "",
                    zeus_bis_source: "theme-product-page",
                  },
                },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: "$shopify:::$default:::" + variantId,
              },
            },
          },
        },
      };

      fetch(KLAVIYO_ENDPOINT + "?company_id=" + encodeURIComponent(companyId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(payload),
      })
        .then((res) => {
          if (res.ok || res.status === 202) {
            this.input.value = "";
            this.input.removeAttribute("aria-invalid");
            this.setStatus(this.dataset.bisSuccess, "success");
          } else {
            this.setStatus(this.dataset.bisError, "error");
          }
        })
        .catch(() => {
          this.setStatus(this.dataset.bisError, "error");
        })
        .finally(() => {
          this._busy = false;
          this.button.disabled = false;
        });
    }
  }

  if (!customElements.get("back-in-stock")) {
    customElements.define("back-in-stock", BackInStock);
  }

  /* ------------------------------------------------------------------
   * Suppress Klaviyo's legacy / app-embed BIS UI when the theme form is
   * active — avoids duplicate "Préviens-moi" + sold-out button stacks.
   * Subscriptions still go to the same Klaviyo BIS API via <back-in-stock>.
   * ------------------------------------------------------------------ */
  var LEGACY_KLAVIYO_BIS_SELECTORS = [
    "#klaviyo-bis-trigger",
    ".klaviyo-bis-trigger",
    "[class*='klaviyo-bis']",
    "[id*='klaviyo-bis']",
    "[data-testid*='back-in-stock']",
    "[data-testid*='back_in_stock']",
  ].join(",");

  function isInsideThemeBis(el) {
    return !!(el && el.closest && el.closest("back-in-stock, .bis-form"));
  }

  function hideLegacyKlaviyoBis(root) {
    var scope = root || document;
    if (!scope.querySelectorAll) return;

    scope.querySelectorAll(LEGACY_KLAVIYO_BIS_SELECTORS).forEach(function (el) {
      if (isInsideThemeBis(el)) return;
      el.setAttribute("hidden", "");
      el.setAttribute("aria-hidden", "true");
      el.style.setProperty("display", "none", "important");
    });

    /* Klaviyo app embed: inline BIS forms injected beside the buy box */
    scope
      .querySelectorAll("buy-buttons ~ .klaviyo-form, .product-info buy-buttons ~ .klaviyo-form")
      .forEach(function (el) {
        el.setAttribute("hidden", "");
        el.setAttribute("aria-hidden", "true");
        el.style.setProperty("display", "none", "important");
      });
  }

  function blockKlaviyoBackinstockEnable() {
    var queue = window.klaviyo;
    if (!queue || queue.__zeusBisGuard) return;

    queue.__zeusBisGuard = true;
    var nativePush = queue.push;
    if (typeof nativePush !== "function") return;

    queue.push = function () {
      var args = Array.prototype.slice.call(arguments);
      if (args[0] === "enable" && args[1] === "backinstock") return queue.length;
      return nativePush.apply(queue, args);
    };
  }

  function initKlaviyoDedup() {
    if (!document.querySelector("back-in-stock")) return;

    document.documentElement.classList.add("zeus-bis-theme");
    blockKlaviyoBackinstockEnable();
    hideLegacyKlaviyoBis();

    if (window.__zeusBisObserver) return;
    window.__zeusBisObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          hideLegacyKlaviyoBis(node);
        });
      });
    });
    window.__zeusBisObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initKlaviyoDedup);
  } else {
    initKlaviyoDedup();
  }
})();
